import isObject from 'lodash/isObject'
import isEqual from 'lodash/isEqual'
import { timeFormatRevert, } from '@/store/helpers'
import { getTypeByPath, } from '@/utils/challenge'
import store from '@/store'

export const processFields = async (challengedFields, directParentLei, ultimateParentLei, fundManagerLei,
  umbrellaFundLei,
  masterFundLei,
  headOfficeLei) => {
  const response = {}
  response.relationshipData = []
  response.referenceData = { fields: [], }
  findNewParentsAndAddTheirLeiCodesToResponse(response, challengedFields)

  for (const challengedField of challengedFields) {
    if (isRelationshipData(challengedField.jsonPath)) {
      addRelationshipDataToResponse(challengedField, response, directParentLei, ultimateParentLei,
        fundManagerLei,
        umbrellaFundLei,
        masterFundLei,
        headOfficeLei)
    } else {
      addReferenceDataToResponse(challengedField, response)
    }
  }

  if (isEqual(response.referenceData, { fields: [], })) {
    delete response.referenceData
  }
  if (isEqual(response.relationshipData, [])) {
    delete response.relationshipData
  }

  await addMandatoryFields(response)

  return response
}


async function addMandatoryFields (response) {
  /**
   * RR_RELATIONSHIP_TYPE or REPEX_CATEGORY must always be passed when relationshipData is challenged.
   * Value depends on parent type and is immutable.
   */
  if (response.relationshipData){
    const { xpath: parentTypeXpath, } = await store.dispatch('record/challenge/getField', 'RR_RELATIONSHIP_TYPE')
    const { xpath: exceptionTypeXpath, } = await store.dispatch('record/challenge/getField', 'REPEX_CATEGORY')
    for (const parentData of response.relationshipData) {
      if (parentData.parent === 'direct') {
        if (parentData.lei) {
          addField({
            'original_value': 'IS_DIRECTLY_CONSOLIDATED_BY',
            'proposed_value': 'IS_DIRECTLY_CONSOLIDATED_BY',
            field: parentTypeXpath,
          }, parentData.fields)
        } else {
          addField({
            'original_value': 'DIRECT_ACCOUNTING_CONSOLIDATION_PARENT',
            'proposed_value': 'DIRECT_ACCOUNTING_CONSOLIDATION_PARENT',
            field: exceptionTypeXpath,
          }, parentData.fields)
        }
      } else if (parentData.parent === 'ultimate') {
        if (parentData.lei) {
          addField({
            'original_value': 'IS_ULTIMATELY_CONSOLIDATED_BY',
            'proposed_value': 'IS_ULTIMATELY_CONSOLIDATED_BY',
            field: parentTypeXpath,
          }, parentData.fields)
        } else {
          addField({
            'original_value': 'ULTIMATE_ACCOUNTING_CONSOLIDATION_PARENT',
            'proposed_value': 'ULTIMATE_ACCOUNTING_CONSOLIDATION_PARENT',
            field: exceptionTypeXpath,
          }, parentData.fields)
        }
      } else if (parentData.parent === 'fund-manager') {
        addField({
          'original_value': 'IS_FUND-MANAGED_BY',
          'proposed_value': 'IS_FUND-MANAGED_BY',
          field: parentTypeXpath,
        }, parentData.fields)
      } else if (parentData.parent === 'umbrella-fund') {
        addField({
          'original_value': 'IS_SUBFUND_OF',
          'proposed_value': 'IS_SUBFUND_OF',
          field: parentTypeXpath,
        }, parentData.fields)
      } else if (parentData.parent === 'master-fund') {
        addField({
          'original_value': 'IS_FEEDER_TO',
          'proposed_value': 'IS_FEEDER_TO',
          field: parentTypeXpath,
        }, parentData.fields)
      } else if (parentData.parent === 'head-office') {
        addField({
          'original_value': 'IS_INTERNATIONAL_BRANCH_OF',
          'proposed_value': 'IS_INTERNATIONAL_BRANCH_OF',
          field: parentTypeXpath,
        }, parentData.fields)
      }
    }
  }
}

function findNewParentsAndAddTheirLeiCodesToResponse (response, challengedFields){
  challengedFields.forEach((field) => {
    if (field.jsonPath === '$.directParent.info.parentEntity') {
      initParentInResponse(response, field['proposed_value'], 'direct')
    } else if (field.jsonPath === '$.ultimateParent.info.parentEntity'){
      initParentInResponse(response, field['proposed_value'], 'ultimate')
    }
    else if (field.jsonPath === '$.fundManager.info.parentEntity'){
      initParentInResponse(response, field['proposed_value'], 'fund-manager')
    }
    else if (field.jsonPath === '$.umbrellaFund.info.parentEntity'){
      initParentInResponse(response, field['proposed_value'], 'umbrella-fund')
    }
    else if (field.jsonPath === '$.masterFund.info.parentEntity'){
      initParentInResponse(response, field['proposed_value'], 'master-fund')
    }
    else if (field.jsonPath === '$.headOffice.info.parentEntity'){
      initParentInResponse(response, field['proposed_value'], 'head-office')
    }
  })
}

function isRelationshipData (jsonPath) {
  const types = [
    'directParent',
    'ultimateParent',
    'fundManager',
    'umbrellaFund',
    'masterFund',
    'headOffice',
  ]
  return types.some((type) => jsonPath.includes(type))
}

function addReferenceDataToResponse (challengedField, response) {
  if (challengedField.highLevel) {
    response.referenceData.highLevel = challengedField.highLevel
  }
  addField(challengedField, response.referenceData.fields)
}

function addRelationshipDataToResponse (challengedField, response, directParentLei, ultimateParentLei,
  fundManagerLei,
  umbrellaFundLei,
  masterFundLei,
  headOfficeLei) {
  if (challengedField.jsonPath.startsWith('$.directParent')) {
    let directParent = response.relationshipData.find((ob) => ob.parent === 'direct')
    if (!directParent) {
      directParent = initParentInResponse(response, directParentLei, 'direct')
    }
    addField(challengedField, directParent.fields)
  } else if (challengedField.jsonPath.startsWith('$.ultimateParent')) {
    let ultimateParent = response.relationshipData.find((ob) => ob.parent === 'ultimate')
    if (!ultimateParent) {
      ultimateParent = initParentInResponse(response, ultimateParentLei, 'ultimate')
    }
    addField(challengedField, ultimateParent.fields)
  } else if (challengedField.jsonPath.startsWith('$.fundManager')) {
    let fundManager = response.relationshipData.find((ob) => ob.parent === 'fund-manager')
    if (!fundManager) {
      fundManager = initParentInResponse(response, fundManagerLei, 'fund-manager')
    }
    addField(challengedField, fundManager.fields)
  } else if (challengedField.jsonPath.startsWith('$.umbrellaFund')) {
    let umbrellaFund = response.relationshipData.find((ob) => ob.parent === 'umbrella-fund')
    if (!umbrellaFund) {
      umbrellaFund = initParentInResponse(response, umbrellaFundLei, 'umbrella-fund')
    }
    addField(challengedField, umbrellaFund.fields)
  } else if (challengedField.jsonPath.startsWith('$.masterFund')) {
    let masterFund = response.relationshipData.find((ob) => ob.parent === 'master-fund')
    if (!masterFund) {
      masterFund = initParentInResponse(response, masterFundLei, 'master-fund')
    }
    addField(challengedField, masterFund.fields)
  } else if (challengedField.jsonPath.startsWith('$.headOffice')) {
    let headOffice = response.relationshipData.find((ob) => ob.parent === 'head-office')
    if (!headOffice) {
      headOffice = initParentInResponse(response, headOfficeLei, 'head-office')
    }
    addField(challengedField, headOffice.fields)
  }
}

function initParentInResponse (response, lei, type) {
  let parent = {
    lei: lei,
    parent: type,
    fields: [],
  }
  response.relationshipData.push(parent)
  return parent
}

function addField (challengedField, fields) {
  // eslint-disable-next-line camelcase
  const { original_value, proposed_value, comment, field, jsonPath, unsetValue, } = challengedField
  fields.push({
    comment: processComment(comment, unsetValue),
    field,
    original_value: processValue(original_value, jsonPath),
    proposed_value: unsetValue ? processValue(original_value, jsonPath) : processValue(proposed_value, jsonPath),
  })
}

function processComment (comment, unsetValue) {
  if (!comment && !unsetValue) {
    return null
  }

  let result = comment || ''
  if (unsetValue) {
    result = '{{unset_value}} ' + result
  }
  return result || null
}

function processValue (value, jsonPath) {
  let result
  if (getTypeByPath(jsonPath) === 'DATE') {
    result = timeFormatRevert(value)
  } else {
    result = isObject(value) ? value.id || value.code || value.lei : value
  }
  return result || null
}



// WEBPACK FOOTER //
// ./src/store/modules/record/modules/challenge/helpers.js